import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Phone, Mail, DollarSign } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";
import { getRoleBadgeStyle } from "./clinicStaffUtils";
import { ReticleSignal, signal } from "@/reticle";

export default function ClinicStaffTab({ clinicId, token, isAdmin, doctorId }) {
    const navigate = useNavigate();
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showAddMember, setShowAddMember] = useState(false);
    const [newMember, setNewMember] = useState({ doctor_phone: '', doctor_name: '', role: 'doctor' });
    const [enableCommission, setEnableCommission] = useState(false);

    // Reset toggle when modal closes
    useEffect(() => {
        if (!showAddMember) {
            setEnableCommission(false);
            setNewMember(prev => ({ ...prev, fee_value: '' }));
        }
    }, [showAddMember]);

    useEffect(() => {
        fetchMembers();
    }, [clinicId, token]);

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/clinics/${clinicId}/members`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMembers(response.data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load members");
        }
    };

    const handleAddMember = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
                ...newMember,
                fee_value: newMember.fee_value ? parseFloat(newMember.fee_value) : undefined
            };
            const response = await axios.post(`${API}/clinics/${clinicId}/members`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            signal(ReticleSignal.CLINIC_MEMBER_ADDED, {
                memberId: response.data?.id,
                clinicId,
                actorId: doctorId,
                role: payload.role
            });
            toast.success("Member added successfully!");
            setShowAddMember(false);
            setNewMember({ doctor_phone: '', doctor_name: '', role: 'doctor' });
            fetchMembers();
        } catch (error) {
            console.error(error);
            let errorMessage = "Failed to add member";
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === 'object' && detail.message) {
                    errorMessage = detail.message;
                } else if (typeof detail === 'object' && Array.isArray(detail)) {
                    errorMessage = detail.map(e => e.msg).join(", ");
                } else if (typeof detail === 'string') {
                    errorMessage = detail;
                }
            }
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveMember = async (memberId) => {
        if (!confirm("Remove this member?")) return;
        try {
            await axios.delete(`${API}/clinics/${clinicId}/members/${memberId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Member removed");
            fetchMembers();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to remove member");
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {isAdmin && (
                <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
                    <DialogTrigger asChild>
                        <Button style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', alignSelf: 'flex-start', marginBottom: '0.25rem' }}>
                            <Plus size={18} style={{ marginRight: '0.5rem' }} />
                            Add Staff
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Add Staff Member</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddMember} style={{ marginTop: '1rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div>
                                    <Label>Phone Number *</Label>
                                    <PhoneInput
                                        value={newMember.doctor_phone}
                                        onChange={(val) => setNewMember({ ...newMember, doctor_phone: val })}
                                        placeholder="Enter phone number"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label>Name (if new)</Label>
                                    <Input
                                        value={newMember.doctor_name}
                                        onChange={(e) => setNewMember({ ...newMember, doctor_name: e.target.value })}
                                        placeholder="Enter name"
                                    />
                                </div>
                                <div>
                                    <Label>Role *</Label>
                                    <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v })}>
                                        <SelectTrigger aria-label="Staff role">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="admin">Admin</SelectItem>
                                            <SelectItem value="doctor">Doctor</SelectItem>
                                            <SelectItem value="consultant">Consultant</SelectItem>
                                            <SelectItem value="receptionist">Receptionist</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {newMember.role === 'consultant' && (
                                    <div className="p-4 bg-muted/50 rounded-lg space-y-4 border border-border">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <DollarSign className="w-4 h-4 text-primary" />
                                                <h4 className="text-sm font-medium">Default Fee Agreement</h4>
                                            </div>
                                            <Switch
                                                checked={enableCommission}
                                                onCheckedChange={(checked) => {
                                                    setEnableCommission(checked);
                                                    if (!checked) {
                                                        setNewMember(prev => ({ ...prev, fee_value: '' }));
                                                    }
                                                }}
                                            />
                                        </div>

                                        {!enableCommission && (
                                            <p className="text-xs text-muted-foreground leading-relaxed">
                                                Enable this to automatically create a facility_charge rule where you (the clinic admin) receive a facility_charge for every appointment this doctor performs.
                                            </p>
                                        )}

                                        {enableCommission && (
                                            <div className="space-y-4 pt-2 animate-in fade-in slide-in-from-top-2">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <Label>Type</Label>
                                                        <Select
                                                            value={newMember.rule_type || "percent"}
                                                            onValueChange={(v) => setNewMember({ ...newMember, rule_type: v })}
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="percent">Percentage (%)</SelectItem>
                                                                <SelectItem value="flat">Flat Amount (₹)</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div>
                                                        <Label>Value</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder={newMember.rule_type === "flat" ? "Amount" : "%"}
                                                            value={newMember.fee_value || ""}
                                                            onChange={(e) => setNewMember({ ...newMember, fee_value: e.target.value })}
                                                            required={enableCommission}
                                                        />
                                                    </div>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    This will create a default facility_charge rule for this doctor in this clinic.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <Button type="submit" disabled={isLoading}>
                                    {isLoading ? 'Adding...' : 'Add Member'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                {members.map(member => {
                    const style = getRoleBadgeStyle(member.role);
                    const showEmail = member.doctor_email && !member.doctor_email.startsWith('placeholder_');
                    const initials = (member.doctor_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                    return (
                        <div key={member.id} className="card"
                            onClick={() => navigate(`/clinics/${clinicId}/staff/${member.id}`)}
                            style={{
                                padding: '1.25rem',
                                position: 'relative',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            {/* Gradient header bar */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: member.role === 'admin'
                                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                    : member.role === 'doctor'
                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                        : member.role === 'consultant'
                                            ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                                            : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                            }} />

                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                                    {/* Avatar with initials */}
                                    <div style={{
                                        width: '52px',
                                        height: '52px',
                                        borderRadius: '50%',
                                        background: member.role === 'admin'
                                            ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                            : member.role === 'doctor'
                                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                                : member.role === 'consultant'
                                                    ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                                                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: '700',
                                        fontSize: '1.125rem',
                                        flexShrink: 0
                                    }}>
                                        {initials}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
                                            {member.doctor_name || 'Unknown'}
                                        </div>
                                        <span style={{
                                            padding: '0.125rem 0.5rem',
                                            borderRadius: '9999px',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            textTransform: 'capitalize',
                                            background: style.bg,
                                            color: style.color
                                        }}>
                                            {member.role}
                                        </span>
                                    </div>
                                </div>
                                {isAdmin && member.user_id !== doctorId && (
                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleRemoveMember(member.id); }} style={{ marginTop: '-0.25rem' }}>
                                        <Trash2 size={16} color="#ef4444" />
                                    </Button>
                                )}
                            </div>

                            {/* Contact info */}
                            <div style={{ marginTop: '0.875rem', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9' }}>
                                {member.doctor_phone && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#64748b', marginBottom: showEmail ? '0.375rem' : 0 }}>
                                        <Phone size={14} />
                                        <span>{member.doctor_phone}</span>
                                    </div>
                                )}
                                {showEmail && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                                        <Mail size={14} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.doctor_email}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

ClinicStaffTab.propTypes = {
    clinicId: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    isAdmin: PropTypes.bool.isRequired,
    doctorId: PropTypes.string.isRequired,
};
